/** Legal & Commercial tab - read-only display of legal identity, payment terms, delivery
 * terms, contract dates and ownership. Demo Mode only, local-only mock data - see
 * `supplierProfileData.ts`. */

import { Box, Grid, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import { getSupplierLegalCommercialInfo } from '../../../../../mocks/data/supplierProfileData';

export interface LegalCommercialTabProps {
  supplierId: number;
}

const Field = ({ label, value }: { label: string; value: string | number }) => (
  <Grid item
    xs={12}
    sm={6}
    md={4}>
    <Typography variant="textXS"
      color="text.secondary">{label}</Typography>
    <Typography variant="textSM">{value || '-'}</Typography>
  </Grid>
);

const LegalCommercialTab: React.FC<LegalCommercialTabProps> = ({ supplierId }) => {
  const { t } = useTranslation('suppliers');
  const info = useMemo(() => getSupplierLegalCommercialInfo(supplierId), [supplierId]);

  return (
    <>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('legalIdentity', 'Legal identity')}</Typography>
          <Grid container
            spacing={3}>
            <Field label={t('legalEntityName', 'Legal entity name')}
              value={info.legalEntityName} />
            <Field label={t('tradingName', 'Trading name')}
              value={info.tradingName} />
            <Field label={t('companyNumber', 'Company number')}
              value={info.companyNumber} />
            <Field label={t('vatNumber', 'VAT number')}
              value={info.vatNumber} />
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('paymentTermsSection', 'Payment terms')}</Typography>
          <Grid container
            spacing={3}>
            <Field label={t('paymentTerms', 'Payment terms')}
              value={info.paymentTerms} />
            <Field label={t('currency', 'Currency')}
              value={info.currency} />
            <Field label={t('minimumOrderValue', 'Minimum order value')}
              value={info.minimumOrderValue} />
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('deliveryTermsSection', 'Delivery terms')}</Typography>
          <Grid container
            spacing={3}>
            <Field label={t('deliveryTerms', 'Delivery terms')}
              value={info.deliveryTerms} />
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('contractDatesSection', 'Contract dates')}</Typography>
          <Grid container
            spacing={3}>
            <Field label={t('contractStartDate', 'Contract start date')}
              value={info.contractStartDate} />
            <Field label={t('contractEndDate', 'Contract end date')}
              value={info.contractEndDate} />
            <Field label={t('noticePeriod', 'Notice period')}
              value={`${info.noticePeriodDays} ${t('days', 'days')}`} />
            <Field label={t('renewalDate', 'Renewal date')}
              value={info.renewalDate} />
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('ownershipSection', 'Ownership')}</Typography>
          <Grid container
            spacing={3}>
            <Field label={t('commercialOwner', 'Commercial owner')}
              value={info.commercialOwner} />
            <Field label={t('procurementOwner', 'Procurement owner')}
              value={info.procurementOwner} />
          </Grid>
        </Box>
      </Section>
    </>
  );
};

export default LegalCommercialTab;
